/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import throttle from "lodash.throttle";
import { editor, Uri, languages } from "monaco-editor/esm/vs/editor/editor.api";
import { FilePath } from "../../../types";
import { isBinaryType, isImageType, isSupportedTextType } from "../../../utils/MediaTypes";


export type ViewMode = "blank" | "notFound" | "image" | "code" | "noPreview" | "unexpected";

export namespace CodeEditorService {

  export const createFileModel = (filePath: string, content: string, onModelChange?: (filePath: FilePath, content: string) => void) => {

    if (!isSupportedTextType(filePath))
      return;

    languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSuggestionDiagnostics: true,
      noSyntaxValidation: true,
    });

    const uri = Uri.file(filePath);
    let model = editor.getModel(uri);
    model?.dispose();
    model = editor.createModel(content, undefined, uri);

    if (model && onModelChange)
      model.onDidChangeContent(throttle(() => onModelChange(filePath, model!.getValue()), 500, { leading: true }));

    languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSuggestionDiagnostics: false,
      noSyntaxValidation: false,
    });
  }

  export const getFileModelValue = (filePath: string) => {
    const uri = Uri.file(filePath);
    const model = editor.getModel(uri);
    return model?.getValue();
  }

  export const removeModels = (filePaths?: string[]) => {
    if (!filePaths)
      editor.getModels().map((model) => model.dispose());
    else {
      for (const filePath of filePaths) {
        editor.getModel(Uri.file(filePath))?.dispose();
      }
    }
  };

  export const openFileInEditor = (textEditor: editor.ICodeEditor, filePath?: string): ViewMode => {
    if (!filePath) {
      textEditor.setModel(null);
      return "blank";
    }

    if (isImageType(filePath)) {
      textEditor.setModel(null);
      return "image";
    }

    if (isBinaryType(filePath)) {
      textEditor.setModel(null);
      return "noPreview";
    }

    const uri = Uri.file(filePath);
    const model = editor.getModel(uri);

    if (!model) {
      textEditor.setModel(null);
      return "notFound";
    }

    if (isSupportedTextType(filePath)) {
      textEditor.setModel(model);
      return "code";
    } else {
      return "unexpected";
    }
  }

  export const setEditorModel = (textEditor: editor.ICodeEditor, filePath?: string) => {
    if (!filePath)
      textEditor.setModel(null);
    else {
      const uri = Uri.file(filePath);
      const model = editor.getModel(uri);
      textEditor.setModel(model);
    }
  }
}
